import React from 'react';
import {CameraView} from 'expo-camera';
import {ColorValue, TextStyle, View, ViewStyle} from 'react-native';
import {Chip, IconButton, Text, useTheme} from 'react-native-paper';
import {useRoute} from '@react-navigation/native';
import {useCameraActions} from '../../../hooks/useCameraActions';

export function CameraControls({
  camera,
}: {
  camera: React.MutableRefObject<CameraView | undefined>;
}) {
  const theme = useTheme();
  const route = useRoute();
  const {chatId, folderId} = route.params as {
    chatId?: string;
    folderId: string;
  };
  const {
    stopRecording,
    startVideo,
    toggleCameraFacing,
    makePhoto,
    cameraMode,
    recording,
    selectImage,
    switchToPhoto,
    switchToVideo,
  } = useCameraActions(chatId || folderId, camera);

  const activeColor = theme.colors.onSurface;
  const inactiveColor = theme.colors.surface;
  const activeChipStyles: ViewStyle = chipStyles(activeColor);
  const chipContainer: ViewStyle = chipContainerStyle(
    theme.colors.surface,
  );
  const chipTextStyles: TextStyle = chipTextStyle(
    theme.colors.onPrimary,
  );
  const actionIcon = recording ? 'square' : 'circle';
  const inactiveChipStyles = {
    ...activeChipStyles,
    backgroundColor: inactiveColor,
  };
  const inactiveChipTextStyles = {
    ...chipTextStyles,
    color: theme.colors.primary,
  };

  const videoAction = recording ? stopRecording : startVideo;
  const onPressAction =
    cameraMode === 'photo' ? makePhoto : videoAction;

  return (
    <View style={CameraControlsContainer}>
      <View style={CameraButtonsContainer}>
        <IconButton
          mode="outlined"
          iconColor="white"
          size={32}
          icon="orbit-variant"
          onPress={toggleCameraFacing}
        />
        <IconButton
          mode="outlined"
          iconColor="white"
          size={48}
          icon={actionIcon}
          style={{marginTop: 0}}
          onPress={onPressAction}
        />
        <IconButton
          mode="outlined"
          iconColor="white"
          size={32}
          icon="image"
          onPress={selectImage}
        />
      </View>
      <View style={chipContainer}>
        <Chip
          style={
            cameraMode === 'photo'
              ? activeChipStyles
              : inactiveChipStyles
          }
          onPress={switchToPhoto}>
          <Text
            style={
              cameraMode === 'photo'
                ? chipTextStyles
                : inactiveChipTextStyles
            }>
            {'Camera'}
          </Text>
        </Chip>
        <Chip
          style={
            cameraMode === 'video'
              ? activeChipStyles
              : inactiveChipStyles
          }
          onPress={switchToVideo}>
          <Text
            style={
              cameraMode === 'video'
                ? chipTextStyles
                : inactiveChipTextStyles
            }>
            {'Video'}
          </Text>
        </Chip>
      </View>
    </View>
  );
}

const chipStyles = (activeColor: ColorValue) =>
  ({
    width: 100,
    height: 30,
    borderRadius: 30,
    alignContent: 'center',
    backgroundColor: activeColor,
    paddingBottom: 2,
  } as ViewStyle);

const chipContainerStyle = (backgroundColor: ColorValue) =>
  ({
    height: 150,
    backgroundColor,
    width: '100%',
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingTop: 25,
    gap: 10,
    marginVertical: 15,
  } as ViewStyle);

const chipTextStyle = (color: ColorValue) =>
  ({
    textAlign: 'center',
    width: 65,
    height: 20,
    color,
    fontWeight: 'bold',
  } as TextStyle);

const CameraControlsContainer: ViewStyle = {
  height: 180,
  width: '100%',
  position: 'absolute',
  bottom: 0,
  zIndex: 1000,
};

const CameraButtonsContainer: ViewStyle = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: 48,
};
